import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Braces, Eye, PanelsTopLeft } from 'lucide-react';
import { RightPanel } from './components/RightPanel';
import { Tabs } from './components/Tabs';
import { ToastRegion, type ToastMessage } from './components/Toast';
import { Toolbar } from './components/Toolbar';
import type {
  AppLanguage,
  AppTheme,
  IssueField,
  IssueFieldType,
  IssueForm,
  PresetId,
  RepositorySettings,
} from './domain/types';
import {
  createDefaultRepositorySettings,
  createField,
  duplicateField,
  getFieldIds,
} from './domain/utils';
import { getDefaultForm, getPreset } from './domain/presets';
import {
  validateIssueForm,
  validateRepositorySettings,
  type ValidationIssue,
} from './domain/validation';
import { EditorPanel } from './features/editor/EditorPanel';
import { InspectorPanel } from './features/editor/InspectorPanel';
import { ExportDialog } from './features/import-export/ExportDialog';
import { ImportDialog } from './features/import-export/ImportDialog';
import { IssuePreview } from './features/preview/IssuePreview';
import { ValidationPanel } from './features/validation/ValidationPanel';
import { YamlPanel } from './features/yaml/YamlPanel';
import { translate, type Language, type TranslationKey } from './i18n';
import { clearPersistedState, loadPersistedState, savePersistedState } from './lib/storage';
import { serializeIssueForm } from './lib/yaml';
import {
  canRedo as historyCanRedo,
  canUndo as historyCanUndo,
  createHistoryState,
  historyReducer,
  type HistoryAction,
  type HistoryState,
} from './store/history';
import styles from './App.module.css';

type MobileTab = 'builder' | 'preview' | 'yaml';

interface InitialAppState {
  form: IssueForm;
  language: AppLanguage;
  theme: AppTheme;
  repositorySettings: RepositorySettings;
  restored: boolean;
}

function systemTheme(): AppTheme {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function initialAppState(): InitialAppState {
  const persisted = loadPersistedState();
  if (persisted) {
    return {
      form: persisted.form,
      language: persisted.language,
      theme: persisted.theme,
      repositorySettings: persisted.repositorySettings,
      restored: true,
    };
  }

  return {
    form: getDefaultForm(),
    language: 'en',
    theme: systemTheme(),
    repositorySettings: createDefaultRepositorySettings(),
    restored: false,
  };
}

function useMobileLayout(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia('(max-width: 899px)').matches);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 899px)');
    const update = (event: MediaQueryListEvent) => setMobile(event.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return mobile;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function App() {
  const [initial] = useState<InitialAppState>(initialAppState);

  const [history, dispatchHistory] = useReducer(
    (state: HistoryState<IssueForm>, action: HistoryAction<IssueForm>) =>
      historyReducer(state, action),
    initial.form,
    (form) => createHistoryState(form),
  );
  const [language, setLanguage] = useState<AppLanguage>(initial.language);
  const [theme, setTheme] = useState<AppTheme>(initial.theme);
  const [repositorySettings, setRepositorySettings] = useState<RepositorySettings>(
    initial.repositorySettings,
  );
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [activePreset, setActivePreset] = useState<PresetId | null>(
    initial.restored ? null : 'bug-report',
  );
  const [dirty, setDirty] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('builder');
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const toastCounter = useRef(0);
  const copiedTimer = useRef<number | null>(null);
  const skipNextSave = useRef(false);
  const mobile = useMobileLayout();
  const form = history.present;

  const t = useCallback(
    (key: TranslationKey, values?: Record<string, string | number>) =>
      translate(language as Language, key, values),
    [language],
  );

  const yaml = useMemo(() => serializeIssueForm(form), [form]);
  const validation = useMemo(() => validateIssueForm(form), [form]);
  const selectedField = useMemo(
    () => form.body.find((field) => field.uid === selectedUid) ?? null,
    [form.body, selectedUid],
  );

  const addToast = useCallback((message: string, kind: ToastMessage['kind'] = 'success') => {
    toastCounter.current += 1;
    const id = toastCounter.current;
    setToasts((current) => [...current, { id, message, kind }].slice(-3));
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 2200);
  }, []);

  const commitForm = useCallback((next: IssueForm) => {
    dispatchHistory({ type: 'commit', value: next });
    setDirty(true);
    setActivePreset(null);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dataset.theme = theme;
    document.documentElement.dataset.colorMode = theme;
  }, [language, theme]);

  useEffect(() => {
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    savePersistedState(form, { language, theme, repositorySettings });
  }, [form, language, repositorySettings, theme]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      const key = event.key.toLocaleLowerCase();
      if (key === 'z' && event.shiftKey) {
        event.preventDefault();
        dispatchHistory({ type: 'redo' });
      } else if (key === 'z') {
        event.preventDefault();
        dispatchHistory({ type: 'undo' });
      } else if (key === 'y') {
        event.preventDefault();
        dispatchHistory({ type: 'redo' });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(
    () => () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  const patchField = useCallback(
    (uid: string, update: (field: IssueField) => IssueField) => {
      commitForm({
        ...form,
        body: form.body.map((field) => (field.uid === uid ? update(field) : field)),
      });
    },
    [commitForm, form],
  );

  const addField = (type: IssueFieldType) => {
    const field = createField(type, getFieldIds(form));
    commitForm({ ...form, body: [...form.body, field] });
    setSelectedUid(field.uid);
    addToast(t('addedField', { type: t(type === 'input' ? 'input' : type) }));
  };

  const moveField = (uid: string, direction: -1 | 1) => {
    const index = form.body.findIndex((field) => field.uid === uid);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= form.body.length) return;
    const body = [...form.body];
    const [field] = body.splice(index, 1);
    if (!field) return;
    body.splice(nextIndex, 0, field);
    commitForm({ ...form, body });
  };

  const duplicate = (uid: string) => {
    const index = form.body.findIndex((field) => field.uid === uid);
    const field = form.body[index];
    if (!field) return;
    const copy = duplicateField(field, getFieldIds(form));
    const body = [...form.body];
    body.splice(index + 1, 0, copy);
    commitForm({ ...form, body });
    setSelectedUid(copy.uid);
  };

  const remove = (uid: string) => {
    if (!window.confirm(t('deleteConfirm'))) return;
    commitForm({ ...form, body: form.body.filter((field) => field.uid !== uid) });
    if (selectedUid === uid) setSelectedUid(null);
  };

  const choosePreset = (preset: PresetId) => {
    if (dirty && !window.confirm(t('presetConfirm'))) return;
    const next = getPreset(preset);
    dispatchHistory({ type: 'commit', value: next });
    setSelectedUid(null);
    setActivePreset(preset);
    setDirty(false);
    addToast(t('presetChanged'));
  };

  const copyYaml = async () => {
    try {
      await navigator.clipboard.writeText(yaml);
      setCopied(true);
      addToast(t('copied'));
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      addToast(language === 'zh' ? '无法访问剪贴板。' : 'Clipboard access was denied.', 'info');
    }
  };

  const downloadYaml = () => {
    if (!validation.valid) {
      addToast(t('unsafeExport'), 'info');
      return;
    }
    downloadBlob(new Blob([yaml], { type: 'text/yaml;charset=utf-8' }), form.filename);
    addToast(t('downloadStarted'));
  };

  const exportZip = async () => {
    const repositoryValidation = validateRepositorySettings(repositorySettings);
    if (!validation.valid || !repositoryValidation.valid) {
      addToast(t('unsafeExport'), 'info');
      return;
    }
    setExporting(true);
    try {
      const { createTemplateZipBlob } = await import('./lib/zip');
      const blob = await createTemplateZipBlob(form, repositorySettings);
      downloadBlob(blob, 'issue-mint-template.zip');
      setExportOpen(false);
      addToast(t('zipExported'));
    } catch (error) {
      addToast(error instanceof Error ? error.message : t('unsafeExport'), 'info');
    } finally {
      setExporting(false);
    }
  };

  const importForm = (next: IssueForm) => {
    dispatchHistory({ type: 'commit', value: next });
    setSelectedUid(null);
    setActivePreset(null);
    setDirty(true);
    setImportOpen(false);
    addToast(t('importSuccess'));
  };

  const reset = () => {
    if (!window.confirm(t('resetConfirm'))) return;
    skipNextSave.current = true;
    clearPersistedState();
    const nextForm = getDefaultForm();
    dispatchHistory({ type: 'reset', value: nextForm });
    setLanguage('en');
    setTheme(systemTheme());
    setRepositorySettings(createDefaultRepositorySettings());
    setSelectedUid(null);
    setActivePreset('bug-report');
    setDirty(false);
    addToast(translate('en', 'resetDone'));
  };

  const locateIssue = (issue: ValidationIssue) => {
    setSelectedUid(issue.fieldUid ?? null);
    if (!issue.fieldUid) return;
    window.setTimeout(() => {
      const card = document.querySelector<HTMLElement>(`[data-field-uid="${issue.fieldUid}"]`);
      const target = card?.querySelector<HTMLInputElement>(
        issue.code.includes('.id.') ? '[data-testid="field-id"]' : '[data-testid="field-label"]',
      );
      target?.focus();
      card?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  };

  const editorPanel = (
    <EditorPanel
      form={form}
      selectedUid={selectedUid}
      activePreset={activePreset}
      t={t}
      onPreset={choosePreset}
      onAdd={addField}
      onSelect={setSelectedUid}
      onPatchField={patchField}
      onReorder={(body) => commitForm({ ...form, body })}
      onMove={moveField}
      onDuplicate={duplicate}
      onDelete={remove}
      onImport={() => setImportOpen(true)}
      onExport={() => setExportOpen(true)}
    />
  );

  return (
    <div className={styles.app} data-testid="app">
      <Toolbar
        filename={form.filename}
        theme={theme}
        errors={validation.errors.length}
        warnings={validation.warnings.length}
        canUndo={historyCanUndo(history)}
        canRedo={historyCanRedo(history)}
        t={t}
        onUndo={() => dispatchHistory({ type: 'undo' })}
        onRedo={() => dispatchHistory({ type: 'redo' })}
        onTheme={() => setTheme((current) => (current === 'light' ? 'dark' : 'light'))}
        onLanguage={() => setLanguage((current) => (current === 'en' ? 'zh' : 'en'))}
        onReset={reset}
        onExport={() => setExportOpen(true)}
      />

      {mobile ? (
        <div className={styles.mobileWorkspace}>
          <Tabs
            className={styles.mobileTabs}
            label={t('builder')}
            value={mobileTab}
            onChange={setMobileTab}
            options={[
              {
                id: 'builder',
                label: t('builder'),
                icon: <PanelsTopLeft size={15} strokeWidth={1.8} aria-hidden="true" />,
                testId: 'mobile-builder',
                controls: 'mobile-builder-panel',
              },
              {
                id: 'preview',
                label: t('preview'),
                icon: <Eye size={15} strokeWidth={1.8} aria-hidden="true" />,
                testId: 'mobile-preview',
                controls: 'mobile-preview-panel',
              },
              {
                id: 'yaml',
                label: t('yaml'),
                icon: <Braces size={15} strokeWidth={1.8} aria-hidden="true" />,
                testId: 'mobile-yaml',
                controls: 'mobile-yaml-panel',
              },
            ]}
          />
          <div className={styles.mobileStatus} data-status={validation.valid ? 'ready' : 'error'}>
            {validation.valid
              ? t('ready')
              : t(validation.errors.length === 1 ? 'error' : 'errors', {
                  count: validation.errors.length,
                })}
          </div>
          {mobileTab === 'builder' ? (
            <div id="mobile-builder-panel" role="tabpanel" className={styles.mobilePanel}>
              {editorPanel}
              <InspectorPanel
                form={form}
                selectedField={selectedField}
                t={t}
                onFormChange={commitForm}
                onFieldChange={(field) => patchField(field.uid, () => field)}
              />
              <ValidationPanel
                issues={validation.issues}
                language={language}
                t={t}
                onIssueClick={locateIssue}
              />
            </div>
          ) : null}
          {mobileTab === 'preview' ? (
            <div id="mobile-preview-panel" role="tabpanel" className={styles.mobilePanel}>
              <IssuePreview form={form} t={t} />
            </div>
          ) : null}
          {mobileTab === 'yaml' ? (
            <div id="mobile-yaml-panel" role="tabpanel" className={styles.mobilePanel}>
              <YamlPanel
                yaml={yaml}
                copied={copied}
                t={t}
                onCopy={() => void copyYaml()}
                onDownload={downloadYaml}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className={styles.desktopWorkspace}>
          {editorPanel}
          <IssuePreview form={form} t={t} />
          <RightPanel
            form={form}
            selectedField={selectedField}
            yaml={yaml}
            copied={copied}
            issues={validation.issues}
            language={language}
            t={t}
            onFormChange={commitForm}
            onFieldChange={(field) => patchField(field.uid, () => field)}
            onCopy={() => void copyYaml()}
            onDownload={downloadYaml}
            onIssueClick={locateIssue}
          />
        </div>
      )}

      {importOpen ? (
        <ImportDialog open t={t} onClose={() => setImportOpen(false)} onImport={importForm} />
      ) : null}
      <ExportDialog
        open={exportOpen}
        settings={repositorySettings}
        exporting={exporting}
        t={t}
        onClose={() => setExportOpen(false)}
        onChange={setRepositorySettings}
        onExport={() => void exportZip()}
      />
      <ToastRegion messages={toasts} />
    </div>
  );
}
